import { Injectable, NgZone } from "@angular/core";
import { ConfigService } from "core/config";
import { catchError, Observable, take } from "rxjs";

type EventHandlerMap<TEventKey extends string = string, R extends any = any> = {
    [K in TEventKey]: (message: MessageEvent) => R;
}


@Injectable({ providedIn: 'root' })
export class ServerSideEventsService {

    readonly #apiUrl = this.apiUrl;
    get apiUrl() {
        const { contextRoot, url } = this._configService.appConfig.api
        return [url, contextRoot].join('/')
    }

    get #currentTimestamp() {
        return new Date().getTime()
    }

    constructor(
        private readonly _configService: ConfigService,
        private readonly _zone: NgZone
    ) { }

    subscribeToAnEvent<TEventKey extends string = string, R extends any = any>(
        path: string,
        eventMapper: EventHandlerMap<TEventKey, R>,
        until?: Observable<any>
    ) {



        return this.#wrapObservableInInterval(this.#currentTimestamp, () => {

            const eventSource = new EventSource(this.#getFullUrl(path));
            return new Observable<{ event: TEventKey, payload: R }>(observer => {


                const handlers = Object.entries(eventMapper)
                    .map(([evName, mapper]: [TEventKey, (ev: MessageEvent) => R]) => ({
                        evName,
                        handler: (event: MessageEvent) => {

                            const mapped = mapper(event);
                            observer.next({ event: evName, payload: mapped })

                        }
                    }))

                handlers.forEach(({ evName, handler }) => {
                    eventSource.addEventListener(evName, handler);
                })

                eventSource.onerror = (error) => {
                    console.error(error)

                    this._zone.run(() => {

                        if (eventSource.readyState === eventSource.CLOSED) {
                            observer.error(new Error('Connection closed'))
                            return;
                        }

                        observer.error(error)
                        return;
                    })
                }

                eventSource.onopen = (ev, ...rest) => { };

                if (until) {

                    until.pipe(take(1)).subscribe(() => {
                        observer.complete();
                        if (eventSource.readyState === eventSource.CLOSED) {
                            return
                        }

                        eventSource.close();
                        handlers.forEach(({ evName, handler }) => {
                            eventSource.removeEventListener(evName, handler);
                        })
                    })
                }


                return () => {

                    if (eventSource.readyState === eventSource.CLOSED) {
                        return
                    }

                    handlers.forEach(({ evName, handler }) => {
                        eventSource.removeEventListener(evName, handler);
                    })

                    eventSource.close();
                }

            })
        })
    }

    readonly #wrapObservableInInterval = <T extends any>(lastAttemptTimestamp: number, fn: () => Observable<T>): Observable<T> => fn().pipe(
        catchError((error) => {
            const diff = this.#currentTimestamp - lastAttemptTimestamp

            if (diff < 2 * 1000) {
                throw error;
            }

            return this.#wrapObservableInInterval(this.#currentTimestamp, fn)
        })
    )

    #getFullUrl(path: string) {
        return [this.#apiUrl, path].join('/')
    }
}